import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext'; // useAuth 훅 임포트
import * as CONST from './constants'; // 전역 상수 임포트
import './styles/styles.css';
import styles from './App.module.css';
import CustomDialog from './components/CustomDialog';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import LoginPage from './pages/LoginPage';
import FarmSearchPage from './pages/FarmSearchPage';
import SireSelectionPage from './pages/SireSelectionPage';
import MatingPlanPage from './pages/MatingPlanPage';
import InventoryPage from './pages/InventoryPage';

// --- Axios 기본 설정 ---
// process.env.REACT_APP_API_URL은 .env.production 파일에서 설정됩니다.
// 개발 환경(npm start)에서는 이 값이 없으므로 상대 경로를 사용하고,
// package.json의 proxy가 동작합니다.
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// --- Main Application Component ---

// 초기 상태 상수 정의
const INITIAL_MATING_PLAN_STATE = {
    farmSearchResults: [],
    sireSearchResults: [],
    selectedSires: [],
    selectedCows: [],
    filters: {
        gene: 'ALL',
        management: [],
        prodRatio: 30,
        confRatio: 70,
        country: '한국',
        breed: 'HO',
        ocapaDate: '',
        inbreedingCoefficient: '6.250'
    },
};

const INITIAL_FILTER_SIDEBAR_STATE = {
    filters: null,
    activeTabIndex: 0
};

const App = () => {
    const { isLoggedIn, user } = useAuth(); // Context에서 로그인 상태와 사용자 정보 가져오기
    const [currentPage, setCurrentPage] = useState(CONST.PAGE_FARM_SEARCH);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [selectedFarm, setSelectedFarm] = useState({}); // 초기값 빈 객체
    const [matingPlanState, setMatingPlanState] = useState(INITIAL_MATING_PLAN_STATE);
    const [dialog, setDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, onClose: null });

    // FilterSidebar의 내부 상태(필터, 활성 탭)를 유지하기 위한 상태 (sessionStorage 연동)
    const [filterSidebarState, setFilterSidebarState] = useState(() => {
        const saved = sessionStorage.getItem('filterSidebarState');
        return saved ? JSON.parse(saved) : {
            filters: null,
            activeTabIndex: 0
        };
    });

    // filterSidebarState가 변경될 때마다 sessionStorage에 저장
    useEffect(() => {
        sessionStorage.setItem('filterSidebarState', JSON.stringify(filterSidebarState));
    }, [filterSidebarState]);

    // 화면 크기에 따라 사이드바 축소 상태를 동기화합니다.
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 1024) {
                setSidebarCollapsed(true);
            } else {
                setSidebarCollapsed(false);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // 초기 로드 시 실행

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 로그인 후 처리 로직 (사용자 유형에 따라 페이지 및 농장 설정)
    useEffect(() => {
        if (!isLoggedIn) {
            // 로그아웃 시 상태 초기화
            setCurrentPage(CONST.PAGE_FARM_SEARCH);
            setSelectedFarm({});
            setMatingPlanState(INITIAL_MATING_PLAN_STATE); // 저장된 데이터 초기화
            setFilterSidebarState(INITIAL_FILTER_SIDEBAR_STATE); // 필터 사이드바 초기화
            return;
        }

        // mtype1이 "2"인 농장주 유저인 경우
        if (user?.mtype1 === CONST.USER_TYPE_FARM_OWNER) {
            const fetchFarmData = async () => {
                try {
                    const formData = new FormData();
                    formData.append('hp', user.hp);
                    const response = await axios.post('/api/member', formData);
                    const farmData = response.data?.list?.[0];

                    if (farmData) {
                        setSelectedFarm({ id: farmData.member_id, name: farmData.farm, owner: farmData.name, phone: farmData.hp, gubun: farmData.gubun });
                        setCurrentPage(CONST.PAGE_SIRE_SELECTION);
                    } else {
                        // 농장 정보 조회 실패 시 에러 처리 (필요 시 로그아웃 처리)
                        setDialog({ isOpen: true, title: "오류", message: "농장 정보를 불러오는 데 실패했습니다.", onClose: () => setDialog({ isOpen: false }) });
                    }
                } catch (error) {
                    console.error("농장 정보 조회 실패:", error);
                }
            };
            fetchFarmData();
        }
    }, [isLoggedIn, user]);

    // 선택된 농장 ID
    const currentFarmId = selectedFarm.id;

    // 농장 변경 시 선택된 암소/씨수소 목록 및 필터 사이드바 상태 초기화
    useEffect(() => {
        setMatingPlanState(prev => ({
            ...prev,
            selectedCows: [],
            selectedSires: []
        }));
        setFilterSidebarState(INITIAL_FILTER_SIDEBAR_STATE);
    }, [currentFarmId]);

    // 씨수소 목록 추가 핸들러 (전역에서 사용)
    const handleSireAdd = useCallback((sire) => {
        if (matingPlanState.selectedSires.some(s => s.jm_semencode === sire.jm_semencode)) {
            setDialog({ isOpen: true, title: "중복", message: `'${sire.jm_semencode}'는 이미 교배 목록에 있습니다.`, onClose: () => setDialog({ isOpen: false }) });
            return;
        }

        setMatingPlanState(prev => ({
            ...prev,
            selectedSires: [...prev.selectedSires, sire]
        }));
        // setDialog({ isOpen: true, title: "추가 완료", message: `'${sire.jm_semencode}' 씨수소가 교배 목록에 추가되었습니다.`, onClose: () => setDialog({ isOpen: false }) });
    }, [matingPlanState.selectedSires, setDialog]);


    // 메뉴 이동 전 농장 선택 확인 로직 (Sidebar에서 사용)
    const checkFarmSelectionAndNavigate = (pageId, requiredFarm) => {
        // 사용자가 농장주(mtype1 === "2")가 아니고, 농장 선택이 필요한 페이지에 접근하려 할 때만 체크
        if (user?.mtype1 !== CONST.USER_TYPE_FARM_OWNER && requiredFarm && !selectedFarm.id) {
            setDialog({
                isOpen: true,
                title: "농장 선택 필요",
                message: "씨수소 선택 및 계획교배를 진행하려면 먼저 농장을 선택해야 합니다. 농장검색 페이지로 이동합니다.",
                onConfirm: () => {
                    setDialog({ isOpen: false });
                    setCurrentPage(CONST.PAGE_FARM_SEARCH);
                },
                onClose: () => setDialog({ isOpen: false }),
            });
            return;
        }
        setCurrentPage(pageId);
        setSidebarOpen(false); // 모바일에서 메뉴 선택 시 닫기
    };

    // 메인 콘텐츠 렌더링
    const renderContent = () => {
        switch (currentPage) {
            case CONST.PAGE_FARM_SEARCH:
                return <FarmSearchPage
                    selectedFarm={selectedFarm}
                    setSelectedFarm={setSelectedFarm}
                    searchResults={matingPlanState.farmSearchResults}
                    setSearchResults={(results) => setMatingPlanState(prev => ({ ...prev, farmSearchResults: results }))}
                    setCurrentPage={checkFarmSelectionAndNavigate}
                />;
            case CONST.PAGE_SIRE_SELECTION:
                return <SireSelectionPage
                    selectedFarm={selectedFarm}
                    filteredSires={matingPlanState.sireSearchResults}
                    setFilteredSires={(results) => setMatingPlanState(prev => ({ ...prev, sireSearchResults: results }))}
                    selectedSires={matingPlanState.selectedSires}
                    onSireAdd={handleSireAdd}
                    setPlanState={setMatingPlanState}
                    setDialog={setDialog}
                    filters={matingPlanState.filters}
                    filterSidebarState={filterSidebarState}
                    setFilterSidebarState={setFilterSidebarState}
                    setCurrentPage={checkFarmSelectionAndNavigate}
                />;
            case CONST.PAGE_MATING_PLAN:
                return <MatingPlanPage
                    planState={matingPlanState}
                    setPlanState={setMatingPlanState}
                    selectedFarm={selectedFarm}
                    setDialog={setDialog}
                />;
            case CONST.PAGE_INVENTORY:
                return <InventoryPage
                    selectedFarm={selectedFarm}
                    setDialog={setDialog}
                    planState={matingPlanState}
                />;
            default:
                return <div>페이지를 찾을 수 없습니다.</div>;
        }
    };

    if (!isLoggedIn) {
        return <LoginPage />;
    }

    return (
        <div className={`${styles['app-container']} ${isSidebarCollapsed ? styles['sidebar-collapsed'] : ''}`}>
            {/* Sidebar */}
            <Sidebar
                isSidebarOpen={isSidebarOpen}
                setSidebarOpen={setSidebarOpen}
                currentPage={currentPage}
                setCurrentPage={checkFarmSelectionAndNavigate} // 농장 선택 여부 체크하는 함수 전달
                selectedFarm={selectedFarm}
                isCollapsed={isSidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
            />

            {/* Main Area (Header + Content) */}
            <div className={styles['main-area']}>
                <Header
                    toggleSidebar={() => {
                        setSidebarOpen(prev => !prev);
                        setSidebarCollapsed(false); // 모바일에서 열 때는 항상 펼쳐진 상태로
                    }}
                    selectedFarm={selectedFarm}
                />
                <div className={styles['main-content-area']}>
                    {renderContent()}
                </div>
            </div>

            {/* Custom Dialog / Modal */}
            <CustomDialog
                title={dialog.title}
                message={dialog.message}
                isOpen={dialog.isOpen}
                onClose={dialog.onClose}
                onConfirm={dialog.onConfirm}
            />
        </div>
    );
};

export default App;
